"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  Cloud,
  Code,
  Palette,
  Shield,
  TrendingUp,
  Users,
  ShoppingCart,
  Settings,
  Briefcase,
} from "lucide-react";

const ServiceCard = ({ title, description, icon: Icon, delay = 0 }) => {
  return (
    <div
      className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 min-w-[320px] lg:min-w-[320px] md:min-w-[280px] sm:min-w-[260px] flex-shrink-0"
      style={{
        animationDelay: `${delay}ms`,
      }}
      role="article"
      aria-labelledby={`service-${title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="space-y-4">
        <div className="flex flex-col items-center space-y-3">
          <div className="p-3 bg-white/10 rounded-full">
            <Icon className="w-8 h-8 text-white lg:w-8 lg:h-8 md:w-7 md:h-7 sm:w-6 sm:h-6" />
          </div>
          <h3
            id={`service-${title.replace(/\s+/g, "-").toLowerCase()}`}
            className="  font-bold text-white font-heading text-center leading-tight text-md lg:text-xl md:text-lg sm:text-base"
          >
            {title}
          </h3>
        </div>
        <p className="text-white/70 text-sm font-body leading-[1.3] max-w-[220px] mx-auto text-wrap text-center lg:text-sm md:text-xs sm:text-xs lg:max-w-[220px] md:max-w-[200px] sm:max-w-[180px]">
          {description}
        </p>
      </div>
    </div>
  );
};

const TechBadge = ({ name, delay = 0 }) => {
  return (
    <div
      className="bg-white/15   border-white/30 rounded-full px-4 py-2 text-white/90 text-sm font-medium hover:bg-white/20 transition-all duration-300 lg:px-4 lg:py-2 md:px-3 md:py-1.5 sm:px-3 sm:py-1.5 lg:text-sm md:text-xs sm:text-xs"
      style={{
        animationDelay: `${delay}ms`,
        animation: "slideIn 0.8s ease-out forwards",
      }}
      role="button"
      tabIndex="0"
      aria-label={`Technology: ${name}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          // Handle tech badge interaction
        }
      }}
    >
      {name}
    </div>
  );
};

export default function OurTechnologies() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      title: "Cloud Computing",
      description:
        "SaaS (Software-as-a-Service), Infrastructure as a Service (IaaS), Platform as a Service (PaaS), Public Cloud Vs Private Cloud",
      icon: Cloud,
    },
    {
      title: "Technology",
      description:
        "Applications Development- Desktop and Mobile, Database Management and Administration, Business Intelligence",
      icon: Code,
    },
    {
      title: "SOFTWARE Modern Design",
      description:
        "SOFTWARE Quality Testing, Web Digital Marketing and Search Engine Optimization, Video Game Technology",
      icon: Palette,
    },
    {
      title: "Infrastructure",
      description:
        "Near Field Communication — NFC, Artificial Intelligence, Infrastructure and Cyber Securities Management",
      icon: Shield,
    },
    {
      title: "Online Marketing",
      description:
        "Digital Marketing, Social Media, SEO, SMO, Key Words Search Design",
      icon: TrendingUp,
    },
    {
      title: "Enterprises Consulting",
      description:
        "Enterprises Business Process Management (BPM), Corporate Change Management Consulting, Strategy Management Consulting",
      icon: Users,
    },
    {
      title: "Supply and Sales Design",
      description:
        "Risk Management Implementation Consulting, Pre-Sales Consulting, Supply Change Management Consulting",
      icon: ShoppingCart,
    },
    {
      title: "Management Design",
      description:
        "Workforce Management Consulting, Taxation Consulting, Investment Banking Consulting Services, Procurement Management Consulting",
      icon: Settings,
    },
    {
      title: "Outsourcing Design",
      description:
        "BPO, KPO, LPO, RPO, Staff Augmentation, Back Office, Background Verification",
      icon: Briefcase,
    },
  ];

  const technologies = [
    "React",
    "Next.js",
    "TypeScript",
    "Node.js",
    "Python",
    "AWS",
    "Docker",
    "Kubernetes",
    "TensorFlow",
    "OpenAI",
    "MongoDB",
    "PostgreSQL",
  ];

  // Split services into two rows
  const upperRowServices = services.slice(0, 5);
  const lowerRowServices = services.slice(5);

  return (
    <section
      className="relative py-0 overflow-hidden "
      aria-labelledby="our-technologies-heading"
    >
      <style>{`
        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0px);
          }
        }
        @keyframes scroll-right-to-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        @keyframes scroll-left-to-right {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }
        @keyframes dotFade {
          0%,
          100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.9;
          }
        }
        .scroll-container {
          overflow: hidden;
          white-space: nowrap;
          position: relative;
        }
        .scroll-content-rtl {
          display: inline-flex;
          gap: 1rem;
          animation: scroll-right-to-left 40s linear infinite;
          will-change: transform;
        }
        .scroll-content-ltr {
          display: inline-flex;
          gap: 1rem;
          animation: scroll-left-to-right 45s linear infinite;
          will-change: transform;
        }
        .scroll-container:hover .scroll-content-rtl,
        .scroll-container:hover .scroll-content-ltr {
          animation-play-state: paused;
        }
        /* Touch devices pause on touch */
        .scroll-container:active .scroll-content-rtl,
        .scroll-container:active .scroll-content-ltr {
          animation-play-state: paused;
        }
        .dot {
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          animation: dotFade 3s ease-in-out infinite;
          box-shadow: 0 0 4px rgba(255, 255, 255, 0.6);
        }
        .dot-pattern-tl {
          position: absolute;
          top: 2rem;
          left: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 8px;
          z-index: 10;
        }
        .dot-pattern-br {
          position: absolute;
          bottom: 2rem;
          right: 2rem;
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 8px;
          z-index: 10;
        }
        .dot-pattern-tl .dot:nth-child(n + 1):nth-child(-n + 8) {
          animation-delay: 0s;
          opacity: 0.9;
        }
        .dot-pattern-tl .dot:nth-child(n + 9):nth-child(-n + 16) {
          animation-delay: 0.2s;
          opacity: 0.8;
        }
        .dot-pattern-tl .dot:nth-child(n + 17):nth-child(-n + 24) {
          animation-delay: 0.4s;
          opacity: 0.7;
        }
        .dot-pattern-tl .dot:nth-child(n + 25):nth-child(-n + 32) {
          animation-delay: 0.6s;
          opacity: 0.6;
        }
        .dot-pattern-tl .dot:nth-child(n + 33):nth-child(-n + 40) {
          animation-delay: 0.8s;
          opacity: 0.5;
        }
        .dot-pattern-tl .dot:nth-child(n + 41):nth-child(-n + 48) {
          animation-delay: 1s;
          opacity: 0.4;
        }
        .dot-pattern-tl .dot:nth-child(n + 49):nth-child(-n + 56) {
          animation-delay: 1.2s;
          opacity: 0.3;
        }
        .dot-pattern-tl .dot:nth-child(n + 57):nth-child(-n + 64) {
          animation-delay: 1.4s;
          opacity: 0.2;
        }

        .dot-pattern-br .dot:nth-child(n + 1):nth-child(-n + 8) {
          animation-delay: 1.4s;
          opacity: 0.2;
        }
        .dot-pattern-br .dot:nth-child(n + 9):nth-child(-n + 16) {
          animation-delay: 1.2s;
          opacity: 0.3;
        }
        .dot-pattern-br .dot:nth-child(n + 17):nth-child(-n + 24) {
          animation-delay: 1s;
          opacity: 0.4;
        }
        .dot-pattern-br .dot:nth-child(n + 25):nth-child(-n + 32) {
          animation-delay: 0.8s;
          opacity: 0.5;
        }
        .dot-pattern-br .dot:nth-child(n + 33):nth-child(-n + 40) {
          animation-delay: 0.6s;
          opacity: 0.6;
        }
        .dot-pattern-br .dot:nth-child(n + 41):nth-child(-n + 48) {
          animation-delay: 0.4s;
          opacity: 0.7;
        }
        .dot-pattern-br .dot:nth-child(n + 49):nth-child(-n + 56) {
          animation-delay: 0.2s;
          opacity: 0.8;
        }
        .dot-pattern-br .dot:nth-child(n + 57):nth-child(-n + 64) {
          animation-delay: 0s;
          opacity: 0.9;
        }

        /* Mobile responsive dots */
        @media (max-width: 768px) {
          .dot-pattern-tl {
            display: none;
          }
          .dot-pattern-br {
            bottom: 1rem;
            right: 1rem;
            grid-template-columns: repeat(6, 1fr);
            gap: 6px;
          }
          .dot {
            width: 3px;
            height: 3px;
          }
        }
      `}</style>

      {/* Main Container Box */}
      <div className="mx-2 lg:mx-6 xl:mx-8 md:mx-4 sm:mx-2 bg-gradient-to-br from-[#1719A8] to-[#0F1280] rounded-3xl border border-white/20 backdrop-blur-sm relative">
        <div className="relative overflow-hidden">
          {/* Background Gradient */}
          <div className="absolute inset-0" aria-hidden="true" />

          {/* Accent Glow Effects - Dimmed on mobile */}
          <div
            className="absolute top-10 left-1/2 transform -translate-x-1/2 w-96 h-32 rounded-full blur-3xl opacity-20 lg:opacity-20 md:opacity-15 sm:opacity-5 lg:w-96 lg:h-32 md:w-72 md:h-24 sm:w-48 sm:h-16"
            style={{
              backgroundColor: "#D96F2B",
            }}
            aria-hidden="true"
          />

          <div
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-80 h-40 rounded-full blur-3xl opacity-15 lg:w-80 lg:h-40 md:w-60 md:h-30 sm:w-40 sm:h-20"
            style={{
              backgroundColor: "#C9C51A",
            }}
            aria-hidden="true"
          />

          {/* Decorative Dots - Top Left (Hidden on mobile) */}
          <div className="hidden md:block dot-pattern-tl" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`tl-${i}`} className="dot" />
            ))}
          </div>

          {/* Decorative Dots - Bottom Right */}
          <div className="dot-pattern-br" aria-hidden="true">
            {Array.from({ length: 64 }).map((_, i) => (
              <div key={`br-${i}`} className="dot" />
            ))}
          </div>

          {/* Content Container */}
          <div className="container mx-auto px-4 py-12 sm:py-8 md:py-8 lg:py-12   relative z-20">
            <div
              className={`
              transition-all duration-1000 ease-out
              ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
            `}
            >
              {/* Header Section - Same for all screen sizes */}
              <div className="text-center mb-8 lg:mb-8 md:mb-6 sm:mb-4">
                <div className="inline-flex items-center px-6 py-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-full mb-2 shadow-lg">
                  <span className="w-2 h-2 bg-[#1719A8] rounded-full mr-3 animate-pulse"></span>
                  <span
                    style={{ color: "#1719A8" }}
                    className="text-xs md:text-sm font-semibold uppercase tracking-wider"
                  >
                    Technologies We Use
                  </span>
                </div>
                <header>
                  <h1
                    id="our-technologies-heading"
                    className="text-4xl xl:text-6xl lg:text-5xl md:text-4xl sm:text-2xl font-bold text-white leading-tight font-heading mb-6 lg:mb-6 md:mb-4 sm:mb-3"
                  >
                    Cutting-Edge Technologies <br />
                    <span className="bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] bg-clip-text text-transparent">
                      That Power Innovation
                    </span>
                  </h1>
                  <p className="text-md lg:text-xl md:text-lg sm:text-sm text-white/80 leading-relaxed font-body max-w-3xl mx-auto lg:max-w-3xl md:max-w-2xl sm:max-w-xl">
                    We leverage the latest AI, cloud, and enterprise
                    technologies to build scalable solutions that transform
                    businesses and accelerate digital growth across industries.
                  </p>
                </header>

                {/* Technology Badges - Hidden on mobile */}
                <div
                  className="hidden md:flex flex-wrap gap-3 lg:gap-3 md:gap-2 justify-center pt-4"
                  role="list"
                  aria-label="Featured technologies"
                >
                  {technologies.slice(0, 8).map((tech, index) => (
                    <div key={tech} role="listitem">
                      <TechBadge name={tech} delay={index * 100} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Scrolling Service Cards - Same layout for all screen sizes */}
              <div className="space-y-8 lg:space-y-8 md:space-y-6 sm:space-y-4">
                {/* Upper Row - Right to Left */}
                <div
                  className="scroll-container"
                  role="region"
                  aria-label="Services - Upper row"
                >
                  <div className="scroll-content-rtl">
                    {/* Triple the cards for truly seamless infinite scroll */}
                    {[
                      ...upperRowServices,
                      ...upperRowServices,
                      ...upperRowServices,
                    ].map((service, index) => (
                      <ServiceCard
                        key={`upper-${index}`}
                        title={service.title}
                        description={service.description}
                        icon={service.icon}
                        delay={0}
                      />
                    ))}
                  </div>
                </div>

                {/* Lower Row - Left to Right */}
                {/* <div className="scroll-container" role="region" aria-label="Services - Lower row">
                  <div className="scroll-content-ltr">
                    {[...lowerRowServices, ...lowerRowServices, ...lowerRowServices].map((service, index) => (
                      <ServiceCard
                        key={`lower-${index}`}
                        title={service.title}
                        description={service.description}
                        icon={service.icon}
                        delay={0}
                      />
                    ))}
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
